/* eslint-disable max-len */
import {
    Box,
    Container, Grid, Hidden, Typography,
} from '@mui/material';
import {
    DeleteOutline, EditOutlined, Facebook,
    Pause, PlayArrow, FilterList as FilterListIcon,
    AccountBalanceWalletOutlined as AccountBalanceWalletOutlinedIcon,
} from '@mui/icons-material';

import React from 'react';

import { debounce } from '@mui/material/utils';
import { LoggedInUser } from '../../../types/auth.types';
import { JobViewModel, PostedJob } from '../../../types/jobs.types';
import Pagination from '../components/Pagination/Pagination';
import Anchor from '../foundation/Anchor';
import Button from '../foundation/Button';
import {
    BGContainer, ShadowBGContainer, SecondaryBox, GreenTypography,
} from '../foundation/Containers';
import CustomBR from '../foundation/CustomBR';
import DateFormat from '../foundation/DateFormat';
import { CenteredFlex, Flexbox, VCenteredFlex } from '../foundation/Flex';
import {
    AccountStats,
    IconButtonWhiteBG,
    TagStyle,
} from './UserJobs.page.styles';
import Page from '../layout/base.page';
import TextField from '../foundation/TextField';
import Select from '../foundation/Select/Select';
import UserNotification from '../components/UserNotification';
import {
    deleteJob, editJob, getEmployerJobs,
} from '../../api/job.api';
import {
    getExternalJobImportPreview,
    importExternalJobs,
    type ExternalJobImportPreview,
} from '../../api/extrenalJobs';
import Tooltip from '../foundation/Tooltip';
import {
    ExternalJobImportCapacityDialog,
    NoAvailableJobSlotsDialog,
} from '../components/JobCapacityDialogs';
import get_job_typeEnum from '../../../common/get_job_type.enum';
import UserContext from '../context/user.context';
import { bumpJob } from '../../api/promotions.api';

export type UserJobsProps = {
    url: string
    user: LoggedInUser
    totalPromotions?: number
    totalAllowedJobs?: number
    totalCredits?: number
    totalJobs: number
    userPostedJobs?: PostedJob[],
    isUserRegistered: boolean,
    userHasExternalJobs: boolean,
    tilesMode?: boolean
}

const pageSize = 10;

const sortOptions = [
    { value: 1, text: 'כותרת - עולה' },
    { value: 2, text: 'כותרת - יורד' },
    { value: 3, text: 'תאריך יצירה - עולה' },
    { value: 4, text: 'תאריך יצירה - יורד' },
    { value: 5, text: 'תאריך עדכון - עולה' },
    { value: 6, text: 'תאריך עדכון - יורד' },
    { value: 7, text: 'מספר פניות - עולה' },
    { value: 8, text: 'מספר פניות - יורד' },
];

const mapSortValueToType = {
    1: {
        orderby: 'title',
        direction: 'ASC',
    },
    2: {
        orderby: 'title',
        direction: 'DESC',
    },
    3: {
        orderby: 'created_at',
        direction: 'ASC',
    },
    4: {
        orderby: 'created_at',
        direction: 'DESC',
    },
    5: {
        orderby: 'updated_at',
        direction: 'ASC',
    },
    6: {
        orderby: 'updated_at',
        direction: 'DESC',
    },
    7: {
        orderby: 'numOfApplies',
        direction: 'ASC',
    },
    8: {
        orderby: 'numOfApplies',
        direction: 'DESC',
    },
};

const DEFAULT_SORTBY = 6;

const FACEBOOK_EXTERNAL_DOMAIN = 1;

// const INTERNAL_JOB_PERIOD = 30;

type UserJobItemProps = {
    postedJob: PostedJob
    // onChangeJobTimeClick: (job: PostedJob) => void
    onShowReportsClick: (job: PostedJob) => void
    onFreezeClick: (job: PostedJob) => void
    onUnFreezeClick: (job: PostedJob) => void
    onEditClick: (job: PostedJob) => void
    onDeleteClick: (job: PostedJob) => void
    onBumpJobClick: (job: PostedJob) => void
}

const UserJobItem = React.memo(({ postedJob, ...props }: UserJobItemProps) => {
    const jobEndTime = postedJob.jobEnd ? new Date(postedJob.jobEnd).getTime() : 0;
    const dateDiff = (jobEndTime - Date.now()) / 86400000;
    const isAboutToExpire = dateDiff < 6 && dateDiff > 0;
    const isExpired = Boolean(postedJob.jobEnd && dateDiff < 0);
    const user = React.useContext(UserContext);

    return <><BGContainer bgColor={isExpired ? '#ffe1e1' : ''}>
        <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={5}>
                <Anchor blank href={`/job/details/${postedJob.jobid}`} color="secondary" variant="h6" fontWeight="bold">{postedJob.title}</Anchor>
                <CustomBR h={10} />
                <Box display="flex">
                    <><Button variant="outlined" onClick={() => props.onBumpJobClick(postedJob)}>הקפץ משרה</Button>&nbsp;&nbsp;</>
                    {!!postedJob.promotion && <TagStyle>מקודם</TagStyle>}
                </Box>
                <CustomBR h={10} />
                {!!postedJob.promotion && postedJob.jobEnd && <TagStyle>מקודם עד:&nbsp;<DateFormat date={postedJob.jobEnd} /></TagStyle>}
                {!!(postedJob.user?.email || postedJob.email) && <TagStyle>אימייל:&nbsp;{postedJob.email || postedJob.user?.email}</TagStyle>}
                <TagStyle>ת. עדכון:&nbsp;<DateFormat date={postedJob.updated_at} showTime /></TagStyle>
                <Flexbox flexWrap="wrap">
                    {postedJob.frozen && <TagStyle>מודעה מוקפאת</TagStyle>}
                    {postedJob.employerJobid && <TagStyle><Typography><b>מספר משרה:</b> {postedJob.employerJobid}</Typography></TagStyle>}
                    {postedJob.externalDomain === FACEBOOK_EXTERNAL_DOMAIN && <TagStyle><Tooltip title="משרה זו נלקחה מהפייסבוק ושויכה לחשבון שלך">
                        <VCenteredFlex><Facebook display="inline" htmlColor="#1876f2" /> נמשך מהפייסבוק</VCenteredFlex>
                    </Tooltip></TagStyle>}
                </Flexbox>
            </Grid>
            <Grid item xs={12} sm={12} md={2} display="flex" justifyContent="center">
                <DateFormat date={postedJob.created_at} showTime />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
                {postedJob.jobEnd && <Box display="flex" justifyContent="center">
                    <DateFormat
                        color={isAboutToExpire ? 'red' : ''}
                        date={postedJob.jobEnd}
                        showTime
                    />
                </Box>}
                {isAboutToExpire && <Box display="flex" justifyContent="center">
                    <Typography color="red" fontWeight="bold">לקראת סיום</Typography>
                </Box>}
                {isExpired && <Box display="flex" justifyContent="center">
                    <Typography color="red" fontWeight="bold">הסתיים</Typography>
                </Box>}
            </Grid>
            <Grid item xs={12} sm={12} md={1} display="flex" justifyContent="center">
                <Anchor
                    title={`מספר מועמדויות: ${postedJob.appliedUsers}`}
                    href={`/backoffice/employers/candidates?jobId=${postedJob.jobid}`}
                    text={postedJob.appliedUsers?.toString()}
                    fontWeight="bold" />
            </Grid>
            <Grid item xs={12} sm={12} md={2} display="flex" justifyContent="center">
                <div>
                    <Grid container spacing={2}>
                        <Grid alignItems="center" justifyContent="center" item xs={4}>
                            <IconButtonWhiteBG onClick={() => props.onDeleteClick(postedJob)} title="מחיקת משרה">
                                <DeleteOutline color="secondary" />
                            </IconButtonWhiteBG>
                        </Grid>
                        <Grid item xs={4}>
                            <IconButtonWhiteBG
                                disabled={!user?.admin && isExpired}
                                onClick={() => { if (user?.admin || !isExpired) props.onEditClick(postedJob); }}
                                title="עריכת משרה">
                                <EditOutlined color="secondary" />
                            </IconButtonWhiteBG>
                        </Grid>
                        <Grid item xs={4}>
                            {postedJob.frozen ? <IconButtonWhiteBG
                                disabled={isExpired}
                                onClick={() => props.onUnFreezeClick(postedJob)} title="הפעל משרה">
                                <PlayArrow color="secondary" />
                            </IconButtonWhiteBG> : <IconButtonWhiteBG
                                disabled={isExpired}
                                onClick={() => props.onFreezeClick(postedJob)} title="הקפא משרה"> <Pause color="secondary" />                            </IconButtonWhiteBG>}
                        </Grid>
                    </Grid>
                </div>
            </Grid>
        </Grid>
    </BGContainer><CustomBR /></>;
});

type State = {
    currentPage: number,
    totalJobs: number,
    postedJobs?: PostedJob[]
    filterText: string,
    sortby?: number
    totalAllowedJobs?: number
    totalCredits?: number
    buyJobsModal?: boolean,
    externalJobImportPreview?: ExternalJobImportPreview,
    isImportingExternalJobs: boolean,
    externalJobImportError?: string,
}

export default class UserJobs extends React.Component<UserJobsProps, State> {
    constructor(props) {
        super(props);
        this.state = {
            currentPage: 1,
            totalJobs: props.totalJobs,
            postedJobs: props.userPostedJobs,
            filterText: '',
            sortby: DEFAULT_SORTBY,
            buyJobsModal: false,
            totalAllowedJobs: props.totalAllowedJobs,
            isImportingExternalJobs: false,
        };
    }

    onGoToPage = async page => {
        this.setState({ currentPage: page }, this.filterJobs);
    };

    updateJob = async (jobid: number, data: PostedJob) => {
        const jobRes = await editJob({ jobid }, data, undefined, true);
        if (!jobRes) return;
        this.setState({
            postedJobs: this.state.postedJobs.map(
                job => (jobRes.data.jobid === job.jobid ? { ...job, ...jobRes.data } : job)),
        });
    };

    freezeJobClick = async (job: PostedJob) => {
        await this.updateJob(job.jobid, { ...job, frozen: true });
        if (this.props.tilesMode) {
            this.setState({
                totalAllowedJobs: (this.state.totalAllowedJobs || 0) + 1,
            });
        }
    };

    unfreezeJobClick = async (job: PostedJob) => {
        if (this.props.tilesMode) {
            if ((this.state.totalAllowedJobs || 0) <= 0) {
                this.setState({ buyJobsModal: true });
                return;
            }

            await this.updateJob(job.jobid, { ...job, frozen: false });
            this.setState({
                totalAllowedJobs: Math.max(0, (this.state.totalAllowedJobs || 0) - 1),
            });
        } else {
            await this.updateJob(job.jobid, { ...job, frozen: false });
        }
    };

    showReportsClick = (job: JobViewModel) => {
        window.location.assign(`/job/stats/${job.jobid}`);
    };

    editJobClick = (job: PostedJob) => { window.location.assign(`/user/editJob/${job.jobid}`); };

    onDeleteClick = async (job: PostedJob) => {
        await deleteJob({ jobid: job.jobid }, true);
        this.setState({
            postedJobs: this.state.postedJobs.filter(postedJob => postedJob.jobid !== job.jobid),
        });
        if (this.props.tilesMode) {
            this.setState({
                totalJobs: this.state.totalJobs - 1,
                totalAllowedJobs: (this.state.totalAllowedJobs || 0) + 1,
            });
        }
    };

    runExternalJobImport = async (amount: number) => {
        this.setState({ isImportingExternalJobs: true, externalJobImportError: undefined });
        try {
            await importExternalJobs({ userid: this.props.user.userid, amount });
            window.location.reload();
        } catch {
            this.setState({
                isImportingExternalJobs: false,
                externalJobImportError: 'לא ניתן לייבא את המשרות החיצוניות. יש לרענן את העמוד ולנסות שוב.',
            });
        }
    };

    importFacebookJobs = async () => {
        this.setState({ isImportingExternalJobs: true, externalJobImportError: undefined });
        try {
            const response = await getExternalJobImportPreview({ userid: this.props.user.userid });
            const preview = response.data;

            this.setState({ totalAllowedJobs: preview.availableJobSlots });
            if (preview.totalExternalJobs === 0) {
                this.setState({ isImportingExternalJobs: false });
                return;
            }
            if (preview.missingJobSlots > 0) {
                this.setState({
                    externalJobImportPreview: preview,
                    isImportingExternalJobs: false,
                });
                return;
            }

            await this.runExternalJobImport(preview.totalExternalJobs);
        } catch {
            this.setState({
                isImportingExternalJobs: false,
                externalJobImportError: 'לא ניתן לבדוק כמה משרות חיצוניות אפשר לייבא. יש לרענן את העמוד ולנסות שוב.',
            });
        }
    };

    closeExternalJobImportDialog = () => {
        if (!this.state.isImportingExternalJobs) {
            this.setState({ externalJobImportPreview: undefined });
        }
    };

    buyJobsForImport = () => {
        this.setState({ externalJobImportPreview: undefined });
        window.location.assign('/backoffice/employers/buy-promoted-jobs');
    };

    filterJobs = async () => {
        const {
            sortby, filterText, currentPage,
        } = this.state;

        let type = get_job_typeEnum.USER_POSTED;

        if (window.location.href.indexOf('internalJobs') !== -1) type = get_job_typeEnum.INTERNAL_JOBS;
        if (window.location.href.indexOf('promotedJobs') !== -1) type = get_job_typeEnum.PROMOTED_JOBS;

        const sort = mapSortValueToType[sortby];
        const res = await getEmployerJobs({
            orderby: sort.orderby,
            direction: sort.direction,
            any: filterText,
            type,
            start: (currentPage - 1) * pageSize,
        }, true);

        this.setState({
            postedJobs: res.data.rows,
            totalJobs: res.data.count,
        });
    };

    onFilterTextChangeDebounced = debounce(ev => {
        this.setState({ filterText: ev.target.value }, this.filterJobs);
    }, 500);

    onFilterTextChange = ev => {
        ev.persist();
        this.onFilterTextChangeDebounced(ev);
    };

    onBumpJobBtnClick = async (job: PostedJob) => {
        await bumpJob({ jobid: job.jobid });
        await this.filterJobs();
    };

    onPostJobClick = () => {
        if (!this.props.isUserRegistered) {
            window.showGlobalModal('משתמש לא מאומת', 'נראה כי כתובת המייל שלך עדיין לא מאומתת, על מנת לפרסם משרות יש ללחוץ על הקישור לאימות כתובת האימייל במייל שקיבלת מאיתנו.');
            return;
        }

        if ((this.state.totalAllowedJobs || 0) <= 0) {
            this.setState({ buyJobsModal: true });
            return;
        }

        window.location.assign('/user/postJobs');
    };

    onCloseBuyJobsModal = () => {
        this.setState({ buyJobsModal: !this.state.buyJobsModal });
    };

    render() {
        const { props } = this;
        const {
            totalJobs, currentPage, filterText, sortby, postedJobs,
            buyJobsModal, externalJobImportPreview, isImportingExternalJobs,
            externalJobImportError,
        } = this.state;

        return <Page
            url={props.url}
            user={props.user}
            showBackToTop={false}
            pageHeaderTitle="Mploy - פאנל ניהול משרות" >
            <Container>
                <NoAvailableJobSlotsDialog
                    open={Boolean(buyJobsModal)}
                    onClose={this.onCloseBuyJobsModal}
                />
                {externalJobImportPreview && (
                    <ExternalJobImportCapacityDialog
                        preview={externalJobImportPreview}
                        isImporting={isImportingExternalJobs}
                        onImportPartial={this.runExternalJobImport}
                        onBuyJobs={this.buyJobsForImport}
                        onClose={this.closeExternalJobImportDialog}
                    />
                )}
                <CustomBR />
                {!props.isUserRegistered && <><UserNotification severity="error" id="is-user-registered"><>
                    <span>נראה כי כתובת המייל שלך עדיין לא מאומתת, </span>
                    <span>על מנת לפרסם משרות יש לגשת לעמוד <Anchor href="/backoffice/employers/profile" text="הפרופיל שלי" /> </span>
                    <span>ולסיים את תהליך ההרשמה.</span>
                </></UserNotification><CustomBR /></>}
                {props.userHasExternalJobs && <>{props.isUserRegistered ? <UserNotification severity="warning" id="import-jobs">
                    <Typography>המערכת שלנו מצאה משרות נוספות ברשת המשויכות לכתובת המייל: <b>{props.user.email}</b>.</Typography>
                    <Typography> תוכל לשייך משרות אלה לחשבון שלך בלחיצת כפתור.</Typography>
                    <CustomBR />
                    <Button
                        onClick={this.importFacebookJobs}
                        disabled={isImportingExternalJobs}
                        size="small">
                        {isImportingExternalJobs ? 'בודק כמה משרות ניתן לייבא...' : 'לחץ כאן לייבוא המשרות לחשבון שלך'}
                    </Button>
                </UserNotification> : <UserNotification severity="warning" id="must-registered">
                    <Typography>
                    מצאנו משרות נוספות במערכת המשויכות לכתובת המייל: {props.user.email}, אך חשבונך אינו מאומת.
                על מנת לשייך את המשרות לחשבון שלך, יש ללחוץ על הקישור לאימות החשבון שנשלח אליך במייל בעת ההרשמה.
                    </Typography>
                    <CustomBR />
                    <Anchor blank href="/backoffice/employers/profile" text="הפרופיל שלי" />
                </UserNotification>}<CustomBR /></>}
                {externalJobImportError && <><UserNotification severity="error" id="external-job-import-error">
                    <Typography>{externalJobImportError}</Typography>
                </UserNotification><CustomBR /></>}
                <CenteredFlex>
                    <Typography color="secondary" fontWeight="bold" variant="h4">סה"כ משרות :&nbsp;&nbsp;</Typography>
                    <SecondaryBox>
                        <Typography variant="h6" fontWeight="bold">
                            {totalJobs}
                        </Typography>
                    </SecondaryBox>
                </CenteredFlex>
                <><CustomBR /><AccountStats>
                    <Grid container spacing={0}>
                        {/* <Grid item xs={12} sm={12} md={4}>
                            <CenteredFlex>
                                <AccountBalanceWalletOutlinedIcon />
                                <Tooltip title="כמות הקרדטים שברשוךת לחשיפת קורות חיים">
                                    <Typography color="white">&nbsp;בנק קרדיטים :&nbsp;&nbsp;&nbsp;</Typography>
                                </Tooltip>
                                <div>
                                    <GreenTypography>{this.props.totalCredits || 0}</GreenTypography>
                                </div>
                            </CenteredFlex>
                        </Grid> */}
                        <Grid item xs={12}>
                            <CenteredFlex>
                                <AccountBalanceWalletOutlinedIcon />
                                <Tooltip title="כמות המשרות הזמינות לפרסום בחשבונך">
                                    <Typography color="white">&nbsp;בנק משרות :&nbsp;&nbsp;&nbsp;</Typography>
                                </Tooltip>
                                <div>
                                    <GreenTypography>{Math.max(0, this.state.totalAllowedJobs || 0)}</GreenTypography>
                                </div>
                            </CenteredFlex>

                        </Grid>
                    </Grid>
                </AccountStats></>
                <CustomBR />
                <Flexbox flexDirection="row-reverse">
                    <Button size="large" onClick={() => window.location.assign('/backoffice/employers/buy-promoted-jobs')} variant="outlined">רכישת חבילת משרות</Button>
                    &nbsp;&nbsp;&nbsp;
                    <Button size="large" onClick={this.onPostJobClick} color="primary">פרסום משרה חדשה</Button>
                </Flexbox>
                <CustomBR />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            onChange={this.onFilterTextChange}
                            name="text-filter"
                            id="text-fitler"
                            type="text"
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="סנן משרות לפי כותרת או מספר משרה"
                            InputProps={{
                                endAdornment: <FilterListIcon />,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Select
                            onChange={ev => this.setState({ sortby: parseInt(ev.target.value as string, 10) }, this.filterJobs)}
                            size="small"
                            name="sort-by"
                            fullWidth
                            defaultValue={DEFAULT_SORTBY}
                            options={sortOptions} />
                    </Grid>
                    <Grid item sm={2} />
                    <Grid item xs={12} sm={4}>
                        {totalJobs > 10 && <><Flexbox flexDirection="row-reverse"><Pagination
                            gotoPage={this.onGoToPage}
                            url={props.url}
                            pages={Math.ceil(totalJobs / pageSize)}
                            currentPage={currentPage}
                        /></Flexbox></>}
                    </Grid>
                </Grid>
                {(filterText || sortby) && <div>
                    <CustomBR />
                    {filterText && <Typography color="secondary" display="inline">
                    מסנן משרות לפי טקסט: <b>{filterText}</b>&nbsp;
                    </Typography>}
                    {sortby && <Typography color="secondary" display="inline">
                    סוג מיון: <b>{sortOptions.find(s => s.value === sortby).text}</b>
                    </Typography>}
                    <CustomBR />
                </div>}
                <ShadowBGContainer>
                    <Hidden lgDown>
                        <Grid container padding={2}>
                            <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={5}>כותרת</Grid>
                            <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={2}>תאריך יצירה</Grid>
                            {/* <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={2}>תאריך עדכון</Grid> */}
                            <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={2}>תאריך סיום</Grid>
                            <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={1}>מס' פניות</Grid>
                            <Grid fontWeight="bold" color="secondary" display="flex" justifyContent="center" item lg={2}>פעולות</Grid>
                        </Grid>
                    </Hidden>
                    {postedJobs?.map(postedJob => <UserJobItem
                        // onChangeJobTimeClick={this.onChangeJobTimeClick}
                        onShowReportsClick={this.showReportsClick}
                        onFreezeClick={this.freezeJobClick}
                        onUnFreezeClick={this.unfreezeJobClick}
                        onEditClick={this.editJobClick}
                        onDeleteClick={this.onDeleteClick}
                        onBumpJobClick={this.onBumpJobBtnClick}
                        key={postedJob.jobid}
                        postedJob={postedJob}
                    />)}
                </ShadowBGContainer>
                <CustomBR />
                {totalJobs > 10 && <><Flexbox flexDirection="row-reverse"><Pagination
                    gotoPage={this.onGoToPage}
                    url={props.url}
                    pages={Math.ceil(totalJobs / pageSize)}
                    currentPage={currentPage}
                /></Flexbox><CustomBR /></>}
            </Container>
            {/* <OdooLiveChat /> */}
        </Page>;
    }
}
